<template>
  <v-app>
    <!-- TOP APP BAR -->
    <v-app-bar app flat height="50" dark class="nd-appbar pl-base-bg">
      <!-- Mobile-only — there the drawer is a temporary overlay starting
           hidden, and this hamburger is the only way to open it. Desktop's
           permanent sidebar has its own separate collapse/expand toggle
           inside the drawer header instead (see nd-collapse-btn below). -->
      <v-app-bar-nav-icon v-if="isMobile" @click.stop="drawer = !drawer" />

      <v-btn icon small @click="goBack" class="nd-back-btn ml-3 mr-2">
        <v-icon size="20">mdi-arrow-left</v-icon>
      </v-btn>

      <!-- UPDATE AVAILABLE — hidden until a new deploy is detected -->
      <v-btn
        v-if="updateAvailable"
        small
        class="nd-update-btn mr-1"
        @click="applyUpdate"
      >
        <v-icon left size="16">mdi-cloud-download-outline</v-icon>
        Update Available
      </v-btn>

      <!-- DESKTOP BUTTON -->
      <v-btn
        v-if="!isMobile"
        class="nd-create-btn ml-5"
        small
        @click="createInvoice()"
      >
        <v-icon class="mr-2">mdi-file-document-outline</v-icon>
        Create Invoice
      </v-btn>

      <!-- MOBILE ICON ONLY -->
      <v-btn v-else icon @click="createInvoice()">
        <v-icon>mdi-file-document-outline</v-icon>
      </v-btn>

      <v-spacer />

      <v-chip
        v-if="ownerBusinessName"
        small
        class="nd-owner-chip mr-3"
      >
        <v-icon left size="14">mdi-domain</v-icon>
        Working in {{ ownerBusinessName }}
      </v-chip>

      <h3 v-if="!isMobile" class="mr-2 nd-username">{{ user?.name }}</h3>

      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon v-bind="attrs" v-on="on">
            <v-avatar size="34" color="primary">
              <v-img v-if="user?.profilePicture" :src="user.profilePicture" />
              <span v-else class="white--text text-caption">
                {{ user?.name?.charAt(0)?.toUpperCase() }}
              </span>
            </v-avatar>
          </v-btn>
        </template>

        <v-card width="300">
          <!-- USER INFO -->
          <v-list-item>
            <v-list-item-avatar size="40" color="primary" class="justify-center">
              <v-img v-if="user?.profilePicture" :src="user.profilePicture" />
              <span v-else class="white--text text-subtitle-2">
                {{ user?.name?.charAt(0)?.toUpperCase() }}
              </span>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">
                {{ user?.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                {{ user?.email }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>

          <v-divider />

          <!-- PROFILE -->
          <v-list dense>
            <v-list-item @click="$router.push('/my-account')">
              <v-list-item-icon>
                <v-icon>mdi-account-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Profile</v-list-item-title>
            </v-list-item>

            <!-- LOGOUT -->
            <v-list-item @click="logoutDialog = true">
              <v-list-item-icon>
                <v-icon color="red">mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="red--text"> Logout </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
    </v-app-bar>

    <!-- SIDE NAV — a wide always-visible sidebar on desktop (permanent —
         part of layout, v-main's padding auto-adjusts to its width) and an
         off-canvas slide-in overlay on mobile (temporary — Vuetify's own
         scrim/slide, toggled by the hamburger above). Same accordion list
         renders in both cases; only the drawer's own open/permanent
         behavior differs by breakpoint. -->
    <v-navigation-drawer
      app
      v-model="drawer"
      :permanent="!isMobile"
      :temporary="isMobile"
      :width="isMobile ? 260 : drawerWidth"
      class="nd-drawer"
      :class="{ 'nd-drawer--collapsed': collapsed && !isMobile }"
    >
      <div class="nd-drawer-inner">
        <div class="nd-drawer-header">
          <div class="nd-drawer-logo">
            <img src="@/assets/ProfitLedgex-icon.png" alt="logo" class="nd-icon-img" />
          </div>
          <span class="nd-drawer-brand">ProfitLedgex</span>

          <!-- COLLAPSE — desktop only, and only shown while expanded.
               There's no matching expand button in the collapsed rail —
               clicking any nav row already re-expands the drawer (see
               onNavigate/toggleSection), so a dedicated expand control
               would be redundant. -->
          <v-btn
            v-if="!isMobile && !collapsed"
            icon
            x-small
            class="nd-collapse-btn"
            @click="collapsed = !collapsed"
          >
            <v-icon size="18">mdi-chevron-left</v-icon>
          </v-btn>
        </div>

        <v-divider class="nd-drawer-divider" />

        <div class="nd-nav-list">
          <!-- DASHBOARD — a single real destination, so it's a plain link
               row (one click, straight to /dashboard) rather than an
               accordion section with just one redundant child inside it. -->
          <router-link
            to="/dashboard"
            class="nd-nav-row nd-nav-row--link"
            active-class="nd-nav-row--active"
            @click.native="onNavigate"
          >
            <v-icon size="20" class="nd-nav-row-icon">mdi-view-dashboard-outline</v-icon>
            <span class="nd-nav-row-label">Dashboard</span>
          </router-link>

          <template v-for="section in sectionsBeforeLedger">
            <div
              :key="section.key"
              class="nd-nav-row"
              :class="{ 'nd-nav-row--active': expandedSection !== section.key && currentPageSection === section.key }"
              @click="toggleSection(section.key)"
            >
              <v-icon size="20" class="nd-nav-row-icon">{{ section.icon }}</v-icon>
              <span class="nd-nav-row-label">{{ section.label }}</span>
              <v-icon size="18" class="nd-nav-chevron" :class="{ 'nd-nav-chevron--open': expandedSection === section.key }">
                mdi-chevron-down
              </v-icon>
            </div>

            <v-expand-transition :key="section.key + '-items'">
              <div v-if="expandedSection === section.key" class="nd-nav-children">
                <template v-for="item in section.items">
                  <router-link
                    v-if="!item.guard || passesGuard(item.guard)"
                    :key="item.to"
                    :to="item.to"
                    :exact="item.exact"
                    class="nd-nav-row nd-nav-row--child"
                    active-class="nd-nav-row--active"
                    @click.native="onNavigate"
                  >
                    <span class="nd-badge" :class="'nd-badge--' + item.color"><v-icon size="16">{{ item.icon }}</v-icon></span>
                    <span class="nd-nav-row-label">{{ item.label }}</span>
                  </router-link>
                </template>
              </div>
            </v-expand-transition>
          </template>

          <!-- TRANSACTIONS — a record you manage (edit/delete individual
               cash entries), not a computed summary you read, so it's a
               plain link row rather than an accordion section. -->
          <router-link
            to="/transactions"
            class="nd-nav-row nd-nav-row--link"
            active-class="nd-nav-row--active"
            @click.native="onNavigate"
          >
            <v-icon size="20" class="nd-nav-row-icon">mdi-swap-horizontal</v-icon>
            <span class="nd-nav-row-label">Transactions</span>
          </router-link>

          <template v-for="section in sectionsTransactionsToSupport">
            <div
              :key="section.key"
              class="nd-nav-row"
              :class="{ 'nd-nav-row--active': expandedSection !== section.key && currentPageSection === section.key }"
              @click="toggleSection(section.key)"
            >
              <v-icon size="20" class="nd-nav-row-icon">{{ section.icon }}</v-icon>
              <span class="nd-nav-row-label">{{ section.label }}</span>
              <v-icon size="18" class="nd-nav-chevron" :class="{ 'nd-nav-chevron--open': expandedSection === section.key }">
                mdi-chevron-down
              </v-icon>
            </div>

            <v-expand-transition :key="section.key + '-items'">
              <div v-if="expandedSection === section.key" class="nd-nav-children">
                <template v-for="item in section.items">
                  <router-link
                    v-if="!item.guard || passesGuard(item.guard)"
                    :key="item.to"
                    :to="item.to"
                    :exact="item.exact"
                    class="nd-nav-row nd-nav-row--child"
                    active-class="nd-nav-row--active"
                    @click.native="onNavigate"
                  >
                    <span class="nd-badge" :class="'nd-badge--' + item.color"><v-icon size="16">{{ item.icon }}</v-icon></span>
                    <span class="nd-nav-row-label">{{ item.label }}</span>
                  </router-link>
                </template>
              </div>
            </v-expand-transition>
          </template>

          <!-- MY ACCOUNT — same plain-link treatment as Dashboard, placed
               right after Support and before Admin, still also reachable
               from the top-right avatar menu. -->
          <router-link
            to="/my-account"
            class="nd-nav-row nd-nav-row--link"
            active-class="nd-nav-row--active"
            @click.native="onNavigate"
          >
            <v-icon size="20" class="nd-nav-row-icon">mdi-account-circle-outline</v-icon>
            <span class="nd-nav-row-label">My Account</span>
          </router-link>

          <template v-for="section in sectionsFromAdmin">
            <div
              :key="section.key"
              class="nd-nav-row"
              :class="{ 'nd-nav-row--active': expandedSection !== section.key && currentPageSection === section.key }"
              @click="toggleSection(section.key)"
            >
              <v-icon size="20" class="nd-nav-row-icon">{{ section.icon }}</v-icon>
              <span class="nd-nav-row-label">{{ section.label }}</span>
              <v-icon size="18" class="nd-nav-chevron" :class="{ 'nd-nav-chevron--open': expandedSection === section.key }">
                mdi-chevron-down
              </v-icon>
            </div>

            <v-expand-transition :key="section.key + '-items'">
              <div v-if="expandedSection === section.key" class="nd-nav-children">
                <template v-for="item in section.items">
                  <router-link
                    v-if="!item.guard || passesGuard(item.guard)"
                    :key="item.to"
                    :to="item.to"
                    :exact="item.exact"
                    class="nd-nav-row nd-nav-row--child"
                    active-class="nd-nav-row--active"
                    @click.native="onNavigate"
                  >
                    <span class="nd-badge" :class="'nd-badge--' + item.color"><v-icon size="16">{{ item.icon }}</v-icon></span>
                    <span class="nd-nav-row-label">{{ item.label }}</span>
                  </router-link>
                </template>
              </div>
            </v-expand-transition>
          </template>
        </div>
      </div>
    </v-navigation-drawer>

    <!-- MAIN CONTENT -->
    <v-main class="app-background">
      <div class="app-content">
        <!-- RENEWAL BANNER — shown when subscription is within 10 days of expiry -->
        <v-alert
          v-if="showRenewalBanner"
          type="warning"
          dense
          outlined
          dismissible
          class="nd-renewal-banner"
          @input="renewalBannerDismissed = true"
        >
          <div class="nd-renewal-banner-content">
            <span>
              Your subscription expires in
              {{ daysUntilExpiry }} day{{ daysUntilExpiry === 1 ? "" : "s" }}.
            </span>
            <v-btn
              small
              class="nd-renewal-banner-btn"
              :loading="renewingNow"
              @click="openPlanDialog"
            >
              Renew Now
            </v-btn>
          </div>
        </v-alert>

        <router-view />
      </div>
    </v-main>

    <!-- RENEWAL PLAN SELECTION DIALOG -->
    <global-dialog
      v-model="planDialog"
      :buttons="planDialogButtons"
      title="Choose a renewal plan"
    >
      <div class="nd-plan-options">
        <div
          v-for="plan in renewalPlans"
          :key="plan.id"
          class="nd-plan-option"
          :class="{ 'nd-plan-option--selected': selectedPlanId === plan.id }"
          @click="selectedPlanId = plan.id"
        >
          <span class="nd-plan-label">{{ plan.label }}</span>
          <strong>₹{{ plan.amount }}</strong>
        </div>
      </div>
    </global-dialog>

    <!-- LOGOUT DIALOG -->
    <global-dialog
      v-model="logoutDialog"
      :buttons="buttons"
      title="Confirm Logout"
    >
      <p>Are you sure you want to logout?</p>
    </global-dialog>
  </v-app>
</template>

<script>
import GlobalDialog from "./common/global-dialog.vue";
import { openRazorpayCheckout } from "@/utils/razorpay-checkout";

const RENEWAL_BANNER_WINDOW_DAYS = 10;

// Every top-level rail section and its child pages. `sectionGuard`/item-
// level `guard` are string keys (not functions — this object is a static
// module-level constant, evaluated once at import time with no component
// instance to bind `this` to) resolved against the component's own
// isAdmin/isOwner computed properties at render time — see
// visibleSections()/activeSectionData() below.
const NAV_SECTIONS = [
  {
    key: "sales",
    label: "Sales",
    icon: "mdi-cart-variant",
    items: [
      { to: "/customers", label: "Customers", icon: "mdi-account-multiple-outline", color: "purple" },
      { to: "/products", label: "Products", icon: "mdi-package-variant-closed", color: "orange" },
      { to: "/quotations", label: "Quotations", icon: "mdi-file-document-edit-outline", color: "amber" },
      { to: "/invoices", label: "Invoices", icon: "mdi-file-document-outline", color: "blue" },
      { to: "/credit-notes", label: "Credit Notes", icon: "mdi-file-document-minus-outline", color: "pink" },
    ],
  },
  {
    key: "purchases",
    label: "Purchases",
    icon: "mdi-truck-outline",
    items: [
      { to: "/vendors", label: "Vendors", icon: "mdi-truck-outline", color: "brown" },
      { to: "/purchases", label: "Purchases", icon: "mdi-cart-outline", color: "brown" },
      { to: "/debit-notes", label: "Debit Notes", icon: "mdi-file-document-plus-outline", color: "green" },
    ],
  },
  {
    key: "ledger",
    label: "Accounting",
    icon: "mdi-book-open-variant-outline",
    items: [
      { to: "/chart-of-accounts", label: "Chart of Accounts", icon: "mdi-book-open-variant-outline", color: "blue" },
      { to: "/general-ledger", label: "General Ledger", icon: "mdi-book-search-outline", color: "blue" },
      { to: "/trial-balance", label: "Trial Balance", icon: "mdi-scale-balance", color: "blue" },
      { to: "/balance-sheet", label: "Balance Sheet", icon: "mdi-file-chart-outline", color: "blue" },
      { to: "/fixed-assets", label: "Fixed Assets", icon: "mdi-desk-lamp", color: "brown" },
      { to: "/payroll", label: "Payroll", icon: "mdi-account-cash-outline", color: "brown" },
      { to: "/opening-balances", label: "Opening Balances", icon: "mdi-cash-register", color: "green" },
    ],
  },
  {
    key: "reconciliation",
    label: "Reconciliation",
    icon: "mdi-file-compare",
    items: [
      { to: "/bank-reconciliation", label: "Bank Reconciliation", icon: "mdi-bank-check", color: "blue" },
      { to: "/reconciliation", label: "2A/2B Reconciliation", icon: "mdi-file-compare", color: "teal" },
    ],
  },
  {
    key: "reports",
    label: "Reports",
    icon: "mdi-chart-bar",
    items: [
      { to: "/reports", label: "P&L Report", icon: "mdi-chart-bar", color: "pink" },
      { to: "/accrual-pnl", label: "Accrual P&L", icon: "mdi-chart-line", color: "pink" },
      { to: "/gst-reports", label: "GST Reports", icon: "mdi-file-percent-outline", color: "teal" },
    ],
  },
  {
    key: "business",
    label: "Business",
    icon: "mdi-briefcase-outline",
    sectionGuard: "isOwner",
    items: [
      { to: "/business-profile", label: "Business Profile", icon: "mdi-briefcase-outline", color: "teal" },
      { to: "/bank-account", label: "Bank Account", icon: "mdi-bank-outline", color: "green" },
      { to: "/terms-and-conditions", label: "Terms & Conditions", icon: "mdi-file-sign", color: "orange" },
      { to: "/team-members", label: "Team Members", icon: "mdi-account-group-outline", color: "brown" },
    ],
  },
  {
    key: "support",
    label: "Support",
    icon: "mdi-lifebuoy",
    items: [
      { to: "/support", label: "Support", icon: "mdi-lifebuoy", color: "green" },
      { to: "/tickets", label: "Tickets", icon: "mdi-ticket-outline", color: "pink", guard: "isAdmin" },
      { to: "/help", label: "Help & Guide", icon: "mdi-help-circle-outline", color: "blue" },
    ],
  },
  {
    key: "admin",
    label: "Admin",
    icon: "mdi-shield-crown-outline",
    sectionGuard: "isAdmin",
    items: [
      { to: "/users", label: "Users", icon: "mdi-shield-account-outline", color: "teal", guard: "isAdmin" },
      { to: "/renewal-payments", label: "Payments", icon: "mdi-cash-multiple", color: "orange" },
    ],
  },
];

// Reverse lookup: which section a given route path belongs to — drives
// both the auto-expand-on-navigation watcher and the "you are here"
// header highlight (see currentPageSection below).
const ROUTE_TO_SECTION = {};
NAV_SECTIONS.forEach((section) => {
  section.items.forEach((item) => {
    ROUTE_TO_SECTION[item.to] = section.key;
  });
});

export default {
  name: "NavigationDrawer",
  components: { GlobalDialog },

  data() {
    return {
      // Starts open on desktop (permanent sidebar); on mobile the drawer
      // is a temporary overlay, so opening it by default would cover the
      // page on first load — start closed there instead.
      drawer: window.innerWidth >= 960,
      // Which section is accordion-expanded — null means every section is
      // collapsed. Single-open model (opening one collapses any other),
      // set by an explicit header click (see toggleSection) AND forced by
      // the route watcher below whenever navigation lands on a page inside
      // a section, so the active page's section is always visibly open
      // without requiring a click — matches the old rail's "always
      // highlighted" current-section guarantee.
      expandedSection: null,
      // Desktop-only "give the page more room" toggle — shrinks the
      // sidebar to an icon-only rail (see drawerWidth/nd-drawer--collapsed
      // below). Never applies on mobile, where the drawer is already a
      // temporary overlay dismissed by picking a page, not something you
      // collapse-in-place.
      collapsed: false,
      logoutDialog: false,
      renewalBannerDismissed: false,
      renewingNow: false,
      planDialog: false,
      renewalPlans: [],
      selectedPlanId: null,
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },

    isAdmin() {
      return this.user && this.user.userType === "admin";
    },

    // isOwner/isViewOnly come from the global view-only mixin (main.js).

    ownerBusinessName() {
      return this.user?.ownerBusinessName || null;
    },

    updateAvailable() {
      return this.$store.getters["app/updateAvailable"];
    },

    daysUntilExpiry() {
      if (!this.user?.paymentExpiryDate) return null;
      const ms = new Date(this.user.paymentExpiryDate).getTime() - Date.now();
      return Math.ceil(ms / (24 * 60 * 60 * 1000));
    },

    showRenewalBanner() {
      return (
        !this.isAdmin &&
        !this.renewalBannerDismissed &&
        this.daysUntilExpiry !== null &&
        this.daysUntilExpiry <= RENEWAL_BANNER_WINDOW_DAYS
      );
    },

    buttons() {
      return [
        {
          text: "Cancel",
          action: () => (this.logoutDialog = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Logout",
          action: this.confirmLogout,
          color: "error",
        },
      ];
    },

    planDialogButtons() {
      return [
        {
          text: "Cancel",
          action: () => (this.planDialog = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Continue to Pay",
          action: this.renewNow,
          disabled: !this.selectedPlanId,
          loading: this.renewingNow,
        },
      ];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    // Desktop only — mobile always uses the full 260px inside its
    // temporary overlay (bound directly in the template), regardless of
    // `collapsed`.
    drawerWidth() {
      return this.collapsed ? 72 : 260;
    },

    // Sections filtered by their own sectionGuard (isAdmin/isOwner) — a
    // section with no guard is always shown; one with a guard only shows
    // when that computed property is truthy on the current user.
    visibleSections() {
      return NAV_SECTIONS.filter((section) => !section.sectionGuard || this[section.sectionGuard]);
    },

    // Three direct-link rail items (Dashboard, Transactions, My Account)
    // are interleaved between the flyout sections at fixed points —
    // Transactions before "ledger" (Accounting), My Account after
    // "support" and before "admin" — split the section list around those
    // keys rather than fixed array indices, so it stays correct regardless
    // of which guarded sections (e.g. Business, owner-only) are actually
    // visible for this user.
    sectionsBeforeLedger() {
      const i = this.visibleSections.findIndex((s) => s.key === "ledger");
      return i === -1 ? this.visibleSections : this.visibleSections.slice(0, i);
    },

    sectionsTransactionsToSupport() {
      const start = this.visibleSections.findIndex((s) => s.key === "ledger");
      const end = this.visibleSections.findIndex((s) => s.key === "admin");
      if (start === -1) return [];
      return end === -1 ? this.visibleSections.slice(start) : this.visibleSections.slice(start, end);
    },

    sectionsFromAdmin() {
      const i = this.visibleSections.findIndex((s) => s.key === "admin");
      return i === -1 ? [] : this.visibleSections.slice(i);
    },

    // Which section corresponds to the page you're actually on right now —
    // used both to force that section open (see the $route watcher below)
    // and to keep its header highlighted as "you are here" on the rare
    // occasion it's been manually collapsed again after auto-expanding.
    currentPageSection() {
      return ROUTE_TO_SECTION[this.$route.path] || null;
    },
  },

  watch: {
    // Auto-expand the section containing the active page on every real
    // navigation — preserves the old rail's "always know where you are"
    // guarantee without requiring a click. Deliberately does NOT fight a
    // manual collapse/expand within the same page (no route change), so
    // toggling a different section open to browse it isn't immediately
    // undone. Runs immediately (not just on change) so it also fires once
    // on initial mount via `immediate: true`, covering a fresh page load/
    // deep link straight into e.g. an Invoice detail page.
    "$route.path": {
      immediate: true,
      handler() {
        const section = this.currentPageSection;
        if (section) this.expandedSection = section;
      },
    },
  },

  methods: {
    // Item-level guard resolver — item.guard is a string key ("isAdmin",
    // "isOwner") naming one of this component's own computed properties,
    // not a function (NAV_SECTIONS is a static module-level constant with
    // no component instance to bind a real function's `this` to).
    passesGuard(guardKey) {
      return !!this[guardKey];
    },

    // Single-open accordion: clicking an already-open section's header
    // collapses it again; clicking a different section's header collapses
    // whichever was open and opens the clicked one instead. The whole row
    // (icon + label + chevron) is the click target, not just the chevron.
    //
    // If the sidebar is currently collapsed to icon-only width, a section
    // click re-expands it first rather than trying to render an accordion
    // list with no room for labels — collapse is purely a "give the page
    // more room" toggle, not a second navigation pattern (see the user's
    // explicit choice on this during the collapse feature's design).
    toggleSection(key) {
      if (this.collapsed) {
        this.collapsed = false;
        this.expandedSection = key;
        return;
      }
      this.expandedSection = this.expandedSection === key ? null : key;
    },

    // Picking a leaf item (or a no-children top-level link) closes the
    // whole drawer on mobile, since there it's a temporary overlay covering
    // the page — but must NOT touch `drawer` on desktop, where the sidebar
    // is permanent chrome, not something to dismiss after navigating.
    // Also re-expands a collapsed desktop sidebar, same reasoning as
    // toggleSection above — clicking Dashboard/Transactions/My Account
    // while collapsed shouldn't silently no-op the collapse state.
    onNavigate() {
      if (this.isMobile) this.drawer = false;
      if (this.collapsed) this.collapsed = false;
    },

    async confirmLogout() {
      this.logoutDialog = false;
      await this.$store.dispatch("auth/logout");
      this.$router.replace("/login");
    },

    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      }
    },

    applyUpdate() {
      this.$store.dispatch("app/applyUpdate");
    },

    createInvoice() {
      this.$router.push({ path: "invoices/create" });
    },

    async openPlanDialog() {
      try {
        const config = await this.$store.dispatch("renewal/fetchConfig");
        this.renewalPlans = config.plans || [];
        this.selectedPlanId = config.defaultPlanId || this.renewalPlans[0]?.id || null;
        this.planDialog = true;
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to load renewal plans. Please try again.");
      }
    },

    async renewNow() {
      this.renewingNow = true;
      try {
        const config = await this.$store.dispatch("renewal/fetchConfig");
        // No token needed — createOrder falls back to the active session
        // when no token is supplied (see renewal-controller.js resolveUser).
        const order = await this.$store.dispatch("renewal/createOrder", { planId: this.selectedPlanId });

        this.planDialog = false;

        openRazorpayCheckout({
          order,
          config,
          prefillEmail: this.user?.email,
          onSuccess: async (response) => {
            try {
              await this.$store.dispatch("renewal/verifyPayment", {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
              this.$toast?.success("Subscription renewed!");
              this.renewalBannerDismissed = true;
              // Session's paymentExpiryDate is refreshed from the DB on the
              // very next authenticated request already (see app.js) — a
              // fresh /auth/me pulls the updated value in immediately.
              this.$store.dispatch("auth/restoreSession");
            } catch (err) {
              console.error(err);
              this.$toast?.error("We couldn't verify your payment. Please try again.");
            }
          },
        });
      } catch (err) {
        console.error(err);
        this.$toast?.error("Failed to start renewal payment. Please try again.");
      } finally {
        this.renewingNow = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── App background — soft morning-glory-blue wash ── */
.app-background {
  background: linear-gradient(160deg, #e8f2fc 0%, #f2f8fd 45%, #eaf4fb 100%);
  min-height: 100%;
  /* On mobile, Vuetify recomputes v-main's padding-top (app-bar height)
     via ResizeObserver shortly after first paint. The browser's scroll
     anchoring then "corrects" the resulting layout shift by scrolling
     the page down a few dozen px, clipping content above the fold on
     load. Disabling anchoring here stops that phantom scroll. */
  overflow-anchor: none;
}

/* Single padding value — avoids the doubled-gutter effect from nesting
   a v-row/v-col grid around content that's never actually a grid.
   height anchored to the viewport (not 100% of v-main) so pages like
   the dashboard that fit themselves to the viewport via height:100%
   on their own root element keep resolving correctly. v-main only
   gets `min-height: 100%` above, which is an indefinite height —
   a height:100% child of an indefinite-height parent can resolve to
   auto/0 depending on the exact moment the browser finalizes Vuetify's
   layout pass, which showed up as the due-today grid on the dashboard
   collapsing to zero height on some Windows browsers while working on
   Linux. calc(100vh - <app-bar height>) sidesteps that ancestor-height
   race entirely by anchoring straight to the viewport. */
.app-content {
  padding: 16px;
  height: calc(100vh - 50px);
  box-sizing: border-box;
}

.nd-renewal-banner {
  margin-bottom: 16px;
}

.nd-renewal-banner-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.nd-renewal-banner-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}

.nd-plan-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.nd-plan-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 0;
  cursor: pointer;
  font-size: 15px;
  color: #0f172a;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.nd-plan-option:hover {
  border-color: #0a5a90;
}

.nd-plan-option--selected {
  border-color: #0a5a90;
  background: rgba(10, 90, 144, 0.06);
}

.nd-plan-label {
  font-weight: 600;
}

/* On mobile, or on a viewport too short for a fixed-height no-scroll
   layout to safely fit, pages that opt into that pattern on desktop
   (e.g. dashboard.vue) switch to their natural content height instead —
   let this container grow with them instead of clipping at a fixed
   height. The height threshold here must match dashboard.vue's own
   fallback breakpoint so both switch to natural-height/scroll together. */
@media (max-width: 960px), (max-height: 750px) {
  .app-content {
    height: auto;
    min-height: 100%;
  }
}

.nd-username {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nd-owner-chip.v-chip {
  background: rgba(255, 255, 255, 0.16) !important;
  color: #fff !important;
  font-weight: 600;
  max-width: 260px;
}
.nd-owner-chip.v-chip >>> .v-chip__content {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

/* ── Back button — same frosted-glass treatment as Create Invoice below,
   so the two read as one deliberate button group against the gradient
   app bar instead of a bare unstyled icon floating next to a real button. ── */
.nd-back-btn {
  background: rgba(255, 255, 255, 0.12) !important;
  border: 1px solid rgba(255, 255, 255, 0.28) !important;
  color: #ffffff !important;
}
.nd-back-btn:hover {
  background: rgba(255, 255, 255, 0.22) !important;
  border-color: rgba(255, 255, 255, 0.45) !important;
}

/* ── Create Invoice button (frosted glass, stands out against the gradient app bar) ── */
.nd-create-btn {
  background: rgba(255, 255, 255, 0.15) !important;
  border: 1.5px solid rgba(255, 255, 255, 0.35) !important;
  color: #ffffff !important;
  font-weight: 600 !important;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
}
.nd-create-btn:hover {
  background: rgba(255, 255, 255, 0.24) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* ── Update Available button — deliberately eye-catching: a persistent
   glow plus a light sweep, since this only appears after a deploy and
   customers otherwise have no way to know their app is stale. ── */
.nd-update-btn {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #ffb020 0%, #ff7a1a 100%) !important;
  color: #ffffff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  border: none !important;
  box-shadow:
    0 0 0 rgba(255, 176, 32, 0.5),
    0 2px 8px rgba(0, 0, 0, 0.2) !important;
  animation: nd-update-pulse 2s ease-in-out infinite;
}

.nd-update-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -60%;
  width: 40%;
  height: 100%;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.6) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transform: skewX(-20deg);
  animation: nd-update-shine 2.6s ease-in-out infinite;
}

.nd-update-btn:hover {
  background: linear-gradient(135deg, #ffbf40 0%, #ff8c33 100%) !important;
}

@keyframes nd-update-pulse {
  0%, 100% {
    box-shadow:
      0 0 0 rgba(255, 176, 32, 0.5),
      0 2px 8px rgba(0, 0, 0, 0.2) !important;
  }
  50% {
    box-shadow:
      0 0 12px 3px rgba(255, 176, 32, 0.55),
      0 2px 8px rgba(0, 0, 0, 0.2) !important;
  }
}

@keyframes nd-update-shine {
  0% { left: -60%; }
  60%, 100% { left: 130%; }
}

@media (prefers-reduced-motion: reduce) {
  .nd-update-btn,
  .nd-update-btn::before {
    animation: none;
  }
}

/* ══════════════════════════════
   DRAWER — wide always-visible sidebar (desktop) / off-canvas overlay
   (mobile), full icon+label rows with inline accordion expansion.
══════════════════════════════ */
.nd-drawer {
  border-right: none !important;
  background: var(--pl-base-bg-gradient, linear-gradient(150deg, #052f5e 0%, #0a5a90 35%, #0d8478 68%, #15a355 100%)) !important;
  box-shadow: none !important;
}

/* Column layout: header (logo) pinned at top, non-scrolling; the nav
   list below gets its own independent scroll region — so a long expanded
   section (Accounting, 7 items) plus everything above it can exceed the
   drawer's own height without the header ever scrolling out of view. */
.nd-drawer-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.nd-drawer-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  flex-shrink: 0;
  position: relative;
}

.nd-drawer-divider {
  border-color: rgba(255, 255, 255, 0.16) !important;
}

.nd-drawer-logo {
  width: 28px;
  height: 28px;
  border-radius: 7px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(15, 30, 50, 0.15);
  flex-shrink: 0;
}
.nd-icon-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.nd-drawer-brand {
  font-size: 17px;
  font-weight: 800;
  letter-spacing: -0.01em;
  line-height: 1.1;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
}

.nd-collapse-btn {
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.75) !important;
}

/* ── Collapsed (desktop only) — icon-only rail. Labels, chevrons, the
   brand wordmark, and indented child lists all hide; the drawer itself
   shrinks to drawerCollapsedWidth via the :width binding above. Clicking
   any row while collapsed re-expands the drawer first (see toggleSection/
   onNavigate), so this is purely a "give the page more room" toggle, not
   a second navigation pattern to learn. ── */
.nd-drawer--collapsed .nd-drawer-brand,
.nd-drawer--collapsed .nd-nav-row-label,
.nd-drawer--collapsed .nd-nav-chevron,
.nd-drawer--collapsed .nd-nav-children {
  display: none !important;
}
.nd-drawer--collapsed .nd-drawer-header {
  justify-content: center;
  padding: 14px 8px;
}
.nd-drawer--collapsed .nd-nav-row {
  justify-content: center;
  padding: 10px 0;
}
.nd-drawer--collapsed .nd-badge {
  margin: 0 auto;
}

.nd-nav-list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 8px 10px 16px;
}

/* ── Unified row — used for section headers, direct links (Dashboard/
   Transactions/My Account), and indented child items alike. A row is
   just icon + label (+ optional chevron), full width, single click
   target for the entire row (not just the chevron). ── */
.nd-nav-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  margin: 2px 0;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  font-weight: 500;
  transition: background 0.15s ease;
  box-sizing: border-box;
}

.nd-nav-row-icon {
  color: rgba(255, 255, 255, 0.7);
  flex-shrink: 0;
}

.nd-nav-row-label {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nd-nav-chevron {
  color: rgba(255, 255, 255, 0.55);
  flex-shrink: 0;
  transition: transform 0.2s ease;
}
.nd-nav-chevron--open {
  transform: rotate(180deg);
}

/* Hover — transient, mouse-only, never persists and never overrides the
   active accent bar below (they can stack: hover just layers a slightly
   stronger tint on top of an already-active row). */
.nd-nav-row:hover {
  background: rgba(255, 255, 255, 0.1);
}

/* Active — persistent, route-driven "you are here" marker, independent
   of hover. Left accent bar in solid white + a lighter frosted-glass
   background + fully-opaque white label, applied identically to section
   headers (when their current-page is inside them but they've been
   manually re-collapsed) and to leaf/child/direct-link rows. */
.nd-nav-row--active {
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  font-weight: 600;
  position: relative;
}
.nd-nav-row--active::before {
  content: "";
  position: absolute;
  left: 0;
  top: 6px;
  bottom: 6px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: #ffffff;
}
.nd-nav-row--active .nd-nav-row-icon {
  color: #ffffff;
}

/* Indented child list — sits directly in-flow beneath its section header
   (accordion, not a floating overlay), so it participates in the drawer's
   own scroll region and pushes later rows down exactly like the
   reference pattern. */
.nd-nav-children {
  display: flex;
  flex-direction: column;
  padding-left: 14px;
}
.nd-nav-children .nd-nav-row {
  padding-left: 10px;
  font-size: 13.5px;
}

.nd-badge {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform 0.15s ease;
}

.nd-badge--blue    { background: #e6f0fb; color: #0a5a90; }
.nd-badge--purple  { background: #f1eafb; color: #7c3aed; }
.nd-badge--orange  { background: #fef1e2; color: #d97706; }
.nd-badge--teal    { background: #e3f6f2; color: #0d8478; }
.nd-badge--green   { background: #e6f7ec; color: #15a355; }
.nd-badge--pink    { background: #fdeaf1; color: #db2777; }
.nd-badge--brown   { background: #f3ece4; color: #92572e; }
.nd-badge--amber   { background: #fff3d6; color: #b8720a; }
</style>
